import { memo, useEffect, useState } from 'react';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { isUndefined } from 'lodash';
// Import Store
import { useAppDispatch } from 'store';
import { setCurrencyRates } from 'store/slices/currencyRates';
// Import Routes
import { mainAppRoutes, notFoundRoute } from 'routes/config';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
import useMaintenanceSettings from 'hooks/UseMaintenanceSettings';
// Import UI Components
import { BusyLoader } from '@geneui/components';
// Import Components
import NotActive from 'components/NotActive';
import Preparing from 'components/Preparing';
import UnderMaintenance from 'components/UnderMaintenance';
import RouteLeaveConfirmation from 'components/common/RouteLeaveConfirmation';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { PermissionEnum } from 'enums';

const MainAppRoutes = () => {
    const dispatch = useAppDispatch();
    const { url } = useRouteMatch();
    const { doGetRequest } = useRequest();
    const { hasPermission } = useHasPermission();
    const { isUnderMaintenance, endDate } = useMaintenanceSettings();

    const { getPartnerState, getCurrencyRatesRequest } = useCancelableRequests({
        getPartnerState: UtilsHttpService.getPartnerState,
        getCurrencyRatesRequest: UtilsHttpService.getCurrencyRates,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [partnerState, setPartnerState] = useState<AnyObject>({});

    const init = () => {
        doGetRequest(getPartnerState.request, {
            successCallback: (data) => {
                setPartnerState({ isActivated: data.IsCrmActivated, isPreparing: data.IsPreparing });
                setIsLoading(false);
            },
        });
        doGetRequest(getCurrencyRatesRequest.request, {
            successCallback: (data) => {
                dispatch(setCurrencyRates(data));
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <Switch>
            {mainAppRoutes.map(
                ({ path, component: Component, titleKey, routeLeaveConfirmationType, isExact }, index) => (
                    <Route path={`${url}/${path}`} key={index} exact={isExact}>
                        <BusyLoader
                            isBusy={isLoading}
                            type="spinner"
                            spinnerSize="big"
                            className="crm-full-width crm-justify-content-center"
                        >
                            {!isLoading && (
                                <>
                                    {isUnderMaintenance && !hasPermission([PermissionEnum.CRMAdmin]) ? (
                                        <UnderMaintenance endDate={endDate} />
                                    ) : !partnerState.isActivated &&
                                      !hasPermission([PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]) ? (
                                        <NotActive />
                                    ) : (
                                        <>
                                            {partnerState?.isPreparing ? (
                                                <Preparing />
                                            ) : (
                                                <>
                                                    <Component titleKey={titleKey} />
                                                    {!partnerState.isActivated && (
                                                        <div className="crm-not-active-footer">
                                                            <span>{l.CRMIsNotActivated}</span>
                                                        </div>
                                                    )}
                                                    {!isUndefined(routeLeaveConfirmationType) && (
                                                        <RouteLeaveConfirmation
                                                            routeLeaveConfirmationType={routeLeaveConfirmationType}
                                                            pageName={titleKey}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </BusyLoader>
                    </Route>
                ),
            )}

            <Route component={notFoundRoute.component} />
        </Switch>
    );
};

export default memo(MainAppRoutes);
